//components/dashboard/SeamSuccess.jsx

import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  CircularProgress,
  Snackbar,
  Alert,
} from "@mui/material";
import axios from "axios";
import { associateLockWithProperty } from "../../services/PropertyLockService";

const SeamSuccess = () => {
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState("Processing...");
  const [error, setError] = useState(null);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const navigate = useNavigate();

  const SEAM_API_URL = process.env.REACT_APP_SEAM_API_URL ;


  const handleCopyLink = (propertyId) => {
    const listingUrl = `${window.location.origin}/listing/${propertyId}`;
    navigator.clipboard.writeText(listingUrl)
      .then(() => {
        setOpenSnackbar(true);
      })
      .catch((err) => {
        console.error('Failed to copy URL: ', err);
      });
  };

  const handleCloseSnackbar = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setOpenSnackbar(false);
  };

  useEffect(() => {
    const handleSuccess = async () => {
      const params = new URLSearchParams(window.location.search);
      const uid = params.get("uid");
      const connectWebviewId = params.get("connect_webview_id");

      if (!uid || !connectWebviewId) {
        setError("Missing required parameters.");
        setLoading(false);
        return;
      }

      try {
        console.log("Handling Seam Webview success:", { uid, connectWebviewId });
        setStatus("Connecting smart lock...");

        const response = await axios.post(`${SEAM_API_URL}/api/webview-redirect`, {
          uid,
          connect_webview_id: connectWebviewId,
          success: true,
        });

        console.log("Success response from backend:", response.data);

        // Check if there's a pending property association from PropertyDetailView
        const pendingAssociationStr = sessionStorage.getItem('pendingLockAssociation');

        if (pendingAssociationStr) {
          try {
            const pendingAssociation = JSON.parse(pendingAssociationStr);
            console.log("Found pending lock association:", pendingAssociation);

            // Get the newly added devices from the response
            const devices = response.data.devices || [];
            console.log("Devices from response:", devices);

            if (devices.length > 0 && pendingAssociation.listingId) {
              setStatus("Adding lock to property...");

              // Associate all newly connected devices with the property
              let associatedCount = 0;
              for (const device of devices) {
                const lockId = device.lockId || device.device_id;
                if (lockId) {
                  console.log(`Associating lock ${lockId} with property ${pendingAssociation.listingId}`);
                  try {
                    await associateLockWithProperty(pendingAssociation.listingId, lockId);
                    associatedCount++;
                    console.log("Lock successfully associated with property!");
                  } catch (assocError) {
                    console.error(`Failed to associate lock ${lockId}:`, assocError);
                  }
                }
              }

              // Clear the pending association
              sessionStorage.removeItem('pendingLockAssociation');

              if (associatedCount > 0) {
                // Navigate back to the agent dashboard with success message
                setStatus("Lock added successfully! Redirecting...");
                setTimeout(() => {
                  navigate("/agent-dashboard", {
                    state: {
                      message: `Smart lock added to "${pendingAssociation.propertyTitle}"!`,
                      openPropertyId: pendingAssociation.listingId
                    }
                  });
                }, 1500);
                return;
              }
            }
          } catch (parseError) {
            console.error("Error parsing pending association:", parseError);
            sessionStorage.removeItem('pendingLockAssociation');
          }
        }

        // Default behavior - no pending association
        if (response.data.propertyId) {
          handleCopyLink(response.data.propertyId);
        }

        navigate("/success");
      } catch (error) {
        console.error("Error handling webview redirect:", error.response?.data || error.message);
        setError("Failed to handle the success redirect. Please try again.");
        // Clear any pending association on error
        sessionStorage.removeItem('pendingLockAssociation');
      } finally {
        setLoading(false);
      }
    };

    handleSuccess();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]);

  if (loading) {
    return (
      <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" height="100vh" gap={2}>
        <CircularProgress />
        <Typography variant="body1" color="text.secondary">
          {status}
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="100vh">
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  return (
    <>
      <Snackbar
        open={openSnackbar}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity="success"
          sx={{ width: '100%' }}
        >
          Listing URL copied to clipboard!
        </Alert>
      </Snackbar>
    </>
  );
};

export default SeamSuccess;
